import { styled } from '@material-ui/core';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { IRoom } from '../../../../../Models/IRoom';
import { PolylabelUtils } from '../../../../../Utils/PolylabelUtils';
import { useLaizeCalcDialog, useLaizeCalcDialogState } from '../../LaizeCalcDialogProvider';
import { getMapUIRoomAreaNamePosition, MapUIWidgetProps } from '../MapUIRoomInfos';
import { ProductHelper } from '../../../../../../../Helpers/ProductHelper';
import { qSVG } from 'Plugins/FloorPlan/Utils/qSVG';
import { ObjectUtils } from 'Helpers';
import { getProduitPrincipal } from 'Web/Pages/Quote/Utils/UpdateQuestions';
import { Assets } from 'Web/assets/Assets';
import { ThemeRes } from 'Web/Themes/ThemeRes';

export const MapUIRoomsButtonFlooringDirection = () => {
    const { rooms = [] } = useLaizeCalcDialogState();
    return (
        <g id="mapRoomsButtonFlooringDirection">
            {rooms.map((room) => {
                const centroid = PolylabelUtils.polylabel([room.coords?.map((p) => [p.x, p.y])], 1.0);
                return <MapUIRoomButtonFlooringDirection key={`BoxArea-${room.roomId}`} room={room} pos={centroid} />;
            })}
        </g>
    );
};

export const BUTTON_FLOORING_DIRECTION_WIDTH = 96;
export const MapUIRoomButtonFlooringDirection = ({
    room,
    pos,
    readonly,
}: MapUIWidgetProps & { readonly?: boolean }) => {
    const {
        state: { rooms = [] },
        update,
        laizeResult,
        validCurrentWallStarts: invalidCurrentWallStarts,
    } = useLaizeCalcDialog();

    const onChangeDirection = () => {
        if (laizeResult?.possibleFlooringWallStartByRoomId) {
            const possibleFlooringWallStart = laizeResult.possibleFlooringWallStartByRoomId.find(
                (item) => item.roomId === room.roomId!
            )!.value;
            const currentWallStartIndex = possibleFlooringWallStart.findIndex(
                (w) => w.wallId === room.flooringWallStart?.wallId
            );
            if (currentWallStartIndex !== -1) {
                for (let i = 1; i <= possibleFlooringWallStart.length; i++) {
                    const nextWall = ObjectUtils.atCircular(room.walls, currentWallStartIndex + i);
                    if ((possibleFlooringWallStart.find((w) => w.wallId === nextWall?.wallId)?.laizeResult ?? 0) > 0) {
                        update({
                            rooms: rooms.map((r) => {
                                if (r.roomId === room.roomId) {
                                    r.flooringWallStart = nextWall;
                                }
                                return r;
                            }),
                        });
                        break;
                    }
                }
            }
        }
    };

    const center = getMapUIRoomAreaNamePosition(pos!);
    center.x = center.x - BUTTON_FLOORING_DIRECTION_WIDTH / 2;
    const style = getDirectionIconStyle(room);

    return (
        <ButtonFlooringDirection transform={`translate(${center.x},${center.y + 24})`} onClick={onChangeDirection}>
            <rect y={8} fill="white" width={96} height={35} rx={8} ry={8} strokeWidth="1" stroke="#F2F2F2" />
            <Text text={<FormattedMessage id="LaizeCalcDialog_LaizeDirection_Part1" />} x={12} y={24} />
            <Text text={<FormattedMessage id="LaizeCalcDialog_LaizeDirection_Part2" />} x={12} y={36} />
            {readonly
                ? undefined
                : !invalidCurrentWallStarts.find((x) => x.roomId === room.roomId)?.isValid && (
                      <Assets.ErrorIcon attr={{ x: 50, y: 10 }} />
                  )}
            <g transform={`translate(69,18)`}>
                <g transform={style.transform} style={{ transformOrigin: '7.5px 7.5px' }}>
                    <rect width={20} height={20} fill="transparent" x={-2} y={-2} />
                    <Icon />
                </g>
            </g>
        </ButtonFlooringDirection>
    );
};

const Text = ({ text, x, y }: { text: React.ReactNode; x: number; y: number }) => {
    return (
        <text
            x={x}
            y={y}
            fontSize={11}
            fontWeight={800}
            textAnchor="start"
            fontFamily={ThemeRes.Fonts.fontFamilyNunitoSans}>
            {text}
        </text>
    );
};

const Icon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#ab)">
            <path
                d="m8.325 15.869 2.78-2.78a.46.46 0 0 0 0-.637l-.243-.243a.465.465 0 0 0-.649 0l-1.587 1.576V2.208l1.595 1.584a.46.46 0 0 0 .643 0l.245-.242a.463.463 0 0 0 .002-.638L8.33.132A.44.44 0 0 0 8.008 0a.45.45 0 0 0-.327.131l-2.78 2.78a.463.463 0 0 0 0 .638l.243.242a.435.435 0 0 0 .624 0l1.608-1.622v11.668L5.76 12.208a.44.44 0 0 0-.627 0l-.24.242a.45.45 0 0 0-.131.32c0 .12.047.233.132.318l2.78 2.78a.45.45 0 0 0 .323.132.45.45 0 0 0 .328-.131"
                fill="#E5A033"
            />
        </g>
        <defs>
            <clipPath id="ab">
                <path fill="#fff" d="M16 0v16H0V0z" />
            </clipPath>
        </defs>
    </svg>
);

const ButtonFlooringDirection = styled('g')({ cursor: 'pointer' });

export const usePossibleFlooringDirections = (room: IRoom) => {
    const { produitPrincipal } = getProduitPrincipal(room);
    const productType = ProductHelper.getProductType(produitPrincipal);
    return productType;
};

const getDirectionIconStyle = (room: IRoom) => {
    return {
        transform: `rotate(${qSVG.angleRadToDeg(room.flooringWallStart?.angleRad!) - 90})`,
    };
};
